import {
    forwardRef,
    InputHTMLAttributes,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import Image from 'next/image';

import { InputErrorText } from '@components/elements/shared/InputErrorText/InputErrorText';

import * as S from './styles';
import { ToolTip } from '../Tooltip/Tooltip';
import { Label, LabelTypes } from '../Label/Label';
import { createPortal } from 'react-dom';
import { useElementPosition } from 'src/hooks/useElementPosition';

interface InputProps
    extends Omit<
        InputHTMLAttributes<HTMLInputElement>,
        'disabled' | 'id' | 'placeholder' | 'type' | 'value'
    > {
    label?: React.ReactNode;
    type?: 'text' | 'number' | 'email' | 'password';
    inputId: string;
    error?: string;
    icon?: string;
    loading?: boolean;
    imgAlt?: string;
    showErrorText?: boolean;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    labelType?: LabelTypes;
    height?: string;
    skipLabel?: boolean;
    inputTextCentered?: boolean;
    fullBorder?: boolean;
    iconElement?: React.ReactNode;
    iconComponent?: React.ReactNode;
    informationText?: string;
    placeholder?: string;
    useTooltipPortal?: boolean;
    autoComplete?: string;
    disabled?: boolean;
    value?: string | number;
}

interface InformationTooltipProps {
    informationText: string;
    usePortal: boolean;
    inputRef: React.RefObject<HTMLInputElement | null>;
}

export const InformationTooltip: React.FC<InformationTooltipProps> = ({
    informationText,
    usePortal,
    inputRef,
}: InformationTooltipProps) => {
    const position = useElementPosition(inputRef, {
        enabled: usePortal,
        offset: { top: 10, left: 30 },
    });

    const toolTip = (
        <ToolTip content={informationText} mobileDirection='left' />
    );

    const tooltipContent = (
        <S.PortalBox $top={position.top} $left={position.left}>
            {toolTip}
        </S.PortalBox>
    );

    return usePortal ? (
        createPortal(tooltipContent, document.body)
    ) : (
        <S.InformationBox>{toolTip}</S.InformationBox>
    );
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            label,
            type,
            inputId,
            error,
            icon,
            iconElement,
            imgAlt = '',
            loading,
            onKeyDown,
            height,
            iconComponent,
            informationText,
            labelType = LabelTypes.DEFAULT,
            showErrorText = true,
            skipLabel = false,
            fullBorder = false,
            inputTextCentered = false,
            placeholder = '',
            useTooltipPortal = false,
            disabled = false,
            autoComplete = '',
            value,
            ...rest
        },
        forwardedRef
    ) => {
        const [isHovering, setIsHovering] = useState(false);

        const labelComponent = !skipLabel && (
            <Label
                labelType={labelType}
                inputId={inputId}
                error={!!error}
                label={label}
            />
        );

        const ref = useRef<HTMLInputElement>(null);
        useImperativeHandle(
            forwardedRef,
            () => ref.current as HTMLInputElement
        );

        return (
            <>
                <S.Base
                    $error={!!error}
                    $isHovering={isHovering}
                    $fullBorder={fullBorder}
                    onFocus={() => setIsHovering(true)}
                    onBlur={() => setIsHovering(false)}
                >
                    {labelComponent}
                    <S.Input
                        id={inputId}
                        type={type}
                        ref={ref}
                        onKeyDown={onKeyDown}
                        $height={height}
                        $inputTextCentered={inputTextCentered}
                        placeholder={placeholder}
                        autoComplete={autoComplete}
                        disabled={disabled}
                        value={value}
                        {...rest}
                    />
                    {error && !loading && (
                        <S.Img>
                            <Image
                                src='/static/images/input-error.svg'
                                alt='Inmatningsfel'
                                fill
                                sizes='100vw'
                                style={{
                                    objectFit: 'cover',
                                }}
                            />
                        </S.Img>
                    )}
                    {icon && !error && !loading && (
                        <S.Img>
                            <Image
                                src={icon}
                                alt={imgAlt}
                                width={18}
                                height={21}
                            />
                        </S.Img>
                    )}
                    {iconElement && !error && !loading && (
                        <S.ImgAsFont>{iconElement}</S.ImgAsFont>
                    )}
                    {!error && !loading && iconComponent}
                    {loading && (
                        <S.Img>
                            <Image
                                src='/static/images/loader-teal.svg'
                                alt='Laddar'
                                className='rotate'
                                fill
                                sizes='100vw'
                                style={{
                                    objectFit: 'cover',
                                }}
                            />
                        </S.Img>
                    )}
                    {informationText && (
                        <InformationTooltip
                            informationText={informationText}
                            usePortal={useTooltipPortal}
                            inputRef={ref}
                        />
                    )}
                </S.Base>
                {error && showErrorText && <InputErrorText error={error} />}
            </>
        );
    }
);

Input.displayName = 'Input';
